import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import Reveal from "@/components/Reveal";
import SectionKicker from "@/components/SectionKicker";
import MagneticButton from "@/components/MagneticButton";
import FAQSection from "@/components/FAQSection";
import QuillPreview from "@/components/QuillPreview";
import ScrollConnectedPath, { PathNode } from "@/components/ScrollConnectedPath";
import { useCtaPanel } from "@/components/StartProjectPanel";
import { features, developmentStatus, recordControls, quillFaq, QUILL_CTA } from "@/data/quill.mjs";

const EASE = [0.22, 1, 0.36, 1];

// Legacy filename and test IDs are retained; the public product is Quill.

function FeatureBlock({ feature, i }) {
  const ref = useRef(null);
  const active = useInView(ref, { amount: 0.3 });

  return (
    <div
      ref={ref}
      className="relative"
      data-testid={`schoolos-feature-${i}`}
    >
      <PathNode index={i + 1} active={active} top={18} />
      <Reveal>
        <h3 className="font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
          {feature.title}
        </h3>
        <p className="mt-3 max-w-2xl text-zinc-400 leading-relaxed">{feature.desc}</p>
      </Reveal>
    </div>
  );
}

export default function Quill() {
  const ctaPanel = useCtaPanel();

  return (
    <div data-testid="schoolos-page">
      {/* HERO */}
      <section className="relative pt-32 pb-14 md:pt-52 md:pb-24 overflow-hidden">
        <div className="tech-grid grid-fade absolute inset-0 opacity-40" />
        <div className="glow-orb animate-pulse-glow absolute -top-10 right-[12%] h-[420px] w-[420px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[07]">Product</SectionKicker>
          </Reveal>
          {/* Stacked mask-reveal lines — same pattern as the Payroll hero */}
          <h1 className="mt-6 font-heading font-black uppercase tracking-normal text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] max-w-5xl">
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
              >
                KedByte
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block text-cyan-accent text-glow"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.22, duration: 0.8, ease: EASE }}
              >
                Quill.
              </motion.span>
            </span>
            <span className="sr-only">
              {" "}— school records software in development
            </span>
          </h1>

          {/* Status badge — claims policy: in-development state visible near the top */}
          <Reveal delay={0.18}>
            <div
              className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-cyan-accent/40 bg-cyan-accent/5 px-4 py-2"
              data-testid="schoolos-status-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-accent animate-pulse-glow" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-cyan-accent">
                In development
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-8 max-w-2xl text-zinc-400 text-lg leading-relaxed">
              Quill, by KedByte Technologies Private Limited, is being developed
              for school staff working with pupil records, attendance,
              assessment marks and fee entries.
            </p>
          </Reveal>

          <Reveal delay={0.32}>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <MagneticButton
                onClick={() => ctaPanel.open(QUILL_CTA)}
                testid="schoolos-hero-cta"
              >
                Discuss Quill with KedByte
              </MagneticButton>
              <MagneticButton to="/contact" variant="ghost" testid="schoolos-talk-cta">
                Talk to us
              </MagneticButton>
            </div>
          </Reveal>

          {/* Page-intro hairline draws beneath the header */}
          <motion.div
            className="mt-14 h-px w-full origin-left bg-white/10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
          />
        </div>
      </section>

      {/* WHAT IT IS / WHO IT'S FOR */}
      <section className="pb-14 md:pb-28" data-testid="schoolos-audience">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 md:gap-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-cyan-accent">
              What it is
            </p>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
              School records, in context
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Previously known as SchoolOS. The capabilities on this page
              describe the development build, not a generally available service.
              Selected workflows have been tested using synthetic records;
              broader validation and release approval remain outstanding.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400">
              Who it's for
            </p>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
              School staff
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              For staff maintaining pupil information, recording attendance and
              assessment marks, and handling fee records. This is not a public
              parent application or pupil portal.
            </p>
          </Reveal>
        </div>
      </section>

      <QuillPreview />

      {/* WHAT MAKES IT DIFFERENT — rides the studio's pipeline rail */}
      <section className="relative py-16 md:py-32 border-t border-white/10" data-testid="schoolos-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[01]">Development-build capabilities</SectionKicker>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-4 mb-14 font-heading font-bold tracking-normal text-4xl md:text-5xl text-white max-w-3xl">
              What the development build supports.
            </h2>
          </Reveal>

          <p className="mb-10 max-w-2xl text-zinc-400 leading-relaxed">
            In development. These bounded workflows are not a release or availability offer.
          </p>
          <ScrollConnectedPath>
            <div className="space-y-14 md:space-y-16">
              {features.map((f, i) => (
                <FeatureBlock key={f.title} feature={f} i={i} />
              ))}
            </div>
          </ScrollConnectedPath>
        </div>
      </section>

      <section className="relative py-16 md:py-32 border-t border-white/10" data-testid="schoolos-status">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[02]">Where it stands</SectionKicker>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-4xl md:text-5xl text-white">
              What is implemented. What is not yet available.
            </h2>
            <p className="mt-5 max-w-2xl text-zinc-400 leading-relaxed">
              These distinctions describe the development build and separate
              candidate work, not production availability or a release schedule.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {developmentStatus.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <article className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
                  <p className="font-mono text-xs tracking-[0.16em] uppercase text-cyan-accent">
                    {item.status}
                  </p>
                  <h3 className="mt-4 font-heading font-bold text-2xl text-white">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{item.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-32 border-t border-white/10" data-testid="quill-record-controls">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[03]">Access and record history</SectionKicker>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-4xl md:text-5xl text-white">
              Controls with clear limits.
            </h2>
            <p className="mt-5 max-w-2xl text-zinc-400 leading-relaxed">
              Implemented mechanisms are not proof of regulatory compliance,
              certification or completed security acceptance. This page makes
              no such assurance claim.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {recordControls.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <article className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
                  <h3 className="font-heading font-bold text-2xl text-white">{item.title}</h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{item.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — reuses the site's accordion pattern */}
      <div className="border-t border-white/10 pt-14 md:pt-24">
        <FAQSection
          items={quillFaq}
          kicker="Quill, answered"
          heading="What schools ask us first."
          testid="schoolos-faq"
        />
      </div>

      {/* FINAL CTA */}
      <section className="relative py-16 md:py-40 border-t border-white/10 overflow-hidden" data-testid="schoolos-cta">
        <div className="tech-grid grid-fade absolute inset-0 opacity-30" />
        <div className="glow-orb animate-pulse-glow absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400">
              In development
            </p>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-6 font-heading font-black uppercase text-4xl md:text-6xl text-white max-w-4xl mx-auto leading-[0.95]">
              Discuss your school’s requirements.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex justify-center">
              <MagneticButton
                onClick={() => ctaPanel.open(QUILL_CTA)}
                testid="schoolos-footer-cta"
              >
                Discuss Quill with KedByte
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
